// App User edit. Same TVP2 §9 styling as AdminUserEdit: bordered Paper
// cards, MUI standard inputs, Bootstrap-blue Save / grey Reset.
//
// Two cards rendered:
//   1. Profile         — User Name (locked), First/Last Name, E-mail, Phone
//                        (locked), Status, Role (locked).
//   2. Engineer fields — only when role=engineer. Group / Qualification /
//                        Approval status / Reason.
//
// Password reset is intentionally not inline: the existing magic-link flow
// at /app/app-users/:id/reset is reachable from the per-row Reset icon on
// the list. App-user passwords are owned by the user (mobile flow), so we
// don't expose a direct admin set-password the way we do for admins.

import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { tokens } from "@findgeeks/ui-tokens";
import { hkDistricts } from "@findgeeks/shared";
import { ApiError, apiFetch } from "@/lib/api";
import { useToast } from "@/components/Toast";
import {
  SensitiveDisplay,
  SensitiveTextField,
} from "@/components/SensitiveField";

interface TaxInfo {
  legal_name: string | null;
  hkid: string | null;
  home_address: string | null;
  date_of_birth: string | null;
}

interface LatestBankInfo {
  bank_name: string | null;
  account_holder: string | null;
  account_number: string | null;
  captured_at: string;
}

interface AppUserDetail {
  profile: {
    id: string;
    role: "customer" | "engineer";
    display_name: string | null;
    first_name: string | null;
    last_name: string | null;
    username: string | null;
    email: string | null;
    phone: string | null;
    is_active: boolean;
  };
  engineer_profile: {
    user_id: string;
    group_name: string | null;
    qualification_level: string | null;
    approval_status: string | null;
    bio: string | null;
    work_experience_years: number | null;
    service_areas: string[] | null;
  } | null;
  // Migration 00034 added customer_profiles.group_name. Server returns
  // customer_profiles via select("*"), so the field flows through.
  customer_profile: {
    user_id: string;
    group_name: string | null;
  } | null;
  tax_info: TaxInfo | null;
  latest_bank_info: LatestBankInfo | null;
}

interface FormState {
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  is_active: boolean;
  role: "customer" | "engineer";
  group_name: string;
  qualification_level: string;
  approval_status: string;
  reason: string;
  // Migration 00013 — engineer profile fields editable by both engineer
  // (via mobile onboarding wizard) and admin (here).
  bio: string;
  work_experience_years: string; // string for the input; converted to number on submit
  service_areas: string[];
  // Migration 00032 — admin-only payment & tax info, encrypted at rest.
  legal_name: string;
  hkid: string;
  home_address: string;
  date_of_birth: string; // YYYY-MM-DD
}

function friendlyError(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) {
    return err instanceof Error ? err.message : fallback;
  }
  const code =
    (err.body && typeof err.body === "object" && "error" in err.body
      ? String((err.body as { error?: unknown }).error ?? "")
      : "") || err.message;
  switch (code) {
    case "app_user_not_found":
      return "App user not found.";
    case "engineer_not_found":
    case "engineer_profile_not_found":
      return "Engineer profile not found.";
    case "bad_request":
      return "Invalid input. Double-check the form and try again.";
    case "db_error":
      return "Database error. Try again in a moment.";
    default:
      return err.message || fallback;
  }
}

export function AppUserEdit() {
  const { id = "" } = useParams<{ id: string }>();
  const { show, ToastEl } = useToast();

  const detail = useQuery<AppUserDetail>({
    queryKey: ["app-users", id],
    queryFn: () => apiFetch<AppUserDetail>(`/v1/admin/app-users/${id}`),
    enabled: Boolean(id),
  });

  const splitFromDisplay = (full: string | null) => {
    if (!full) return { first: "", last: "" };
    const ix = full.indexOf(" ");
    if (ix === -1) return { first: full, last: "" };
    return { first: full.slice(0, ix), last: full.slice(ix + 1).trim() };
  };

  const initial = (d: AppUserDetail): FormState => {
    const p = d.profile;
    const eng = d.engineer_profile;
    const cust = d.customer_profile;
    const t = d.tax_info;
    const fallback = splitFromDisplay(p.display_name);
    return {
      username: p.username ?? "",
      first_name: p.first_name ?? fallback.first,
      last_name: p.last_name ?? fallback.last,
      email: p.email ?? "",
      phone: p.phone ?? "",
      is_active: Boolean(p.is_active),
      role: p.role,
      // group_name is per-role: engineer reads engineer_profiles, customer
      // reads customer_profiles. Default to "Primary" so the form doesn't
      // render a blank select for fresh accounts.
      group_name:
        p.role === "engineer"
          ? eng?.group_name ?? "Primary"
          : cust?.group_name ?? "Primary",
      qualification_level: eng?.qualification_level ?? "primary",
      approval_status: eng?.approval_status ?? "pending",
      reason: "",
      bio: eng?.bio ?? "",
      work_experience_years:
        eng?.work_experience_years != null
          ? String(eng.work_experience_years)
          : "",
      service_areas: Array.isArray(eng?.service_areas)
        ? (eng?.service_areas ?? [])
        : [],
      legal_name: t?.legal_name ?? "",
      hkid: t?.hkid ?? "",
      home_address: t?.home_address ?? "",
      date_of_birth: t?.date_of_birth ?? "",
    };
  };

  const [form, setForm] = useState<FormState | null>(null);
  useEffect(() => {
    if (detail.data) setForm(initial(detail.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.data]);

  const original = useMemo(
    () => (detail.data ? initial(detail.data) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [detail.data],
  );

  const patch = useMutation({
    mutationFn: async () => {
      if (!form || !original) return;
      const body: Record<string, unknown> = {};
      if (form.first_name !== original.first_name)
        body.first_name = form.first_name;
      if (form.last_name !== original.last_name)
        body.last_name = form.last_name;
      if (form.email !== original.email) body.email = form.email;
      if (form.is_active !== original.is_active)
        body.is_active = form.is_active;
      if (form.role === "engineer") {
        if (form.qualification_level !== original.qualification_level)
          body.qualification_level = form.qualification_level;
        if (form.approval_status !== original.approval_status) {
          body.approval_status = form.approval_status;
          // reason is required by the engineer-status FSM when moving to
          // 'rejected' (sub-plan 09). Send when present.
          if (form.reason.trim()) body.reason = form.reason.trim();
        }
        if (form.bio !== original.bio) {
          body.bio = form.bio.trim() || null;
        }
        if (form.work_experience_years !== original.work_experience_years) {
          const yrs = Number(form.work_experience_years);
          if (Number.isFinite(yrs) && yrs >= 0 && yrs <= 70) {
            body.work_experience_years = yrs;
          }
        }
        const areasChanged =
          form.service_areas.length !== original.service_areas.length ||
          form.service_areas.some((a, i) => a !== original.service_areas[i]);
        if (areasChanged) body.service_areas = form.service_areas;
      }
      if (Object.keys(body).length === 0) {
        show("No changes to save", "info");
        return;
      }
      await apiFetch(`/v1/admin/app-users/${id}`, { method: "PATCH", body });
    },
    onSuccess: () => {
      show("Profile updated", "success");
      detail.refetch();
    },
    onError: (err) => show(friendlyError(err, "Update failed"), "error"),
  });

  // Group change uses the dedicated /group endpoint so it has its own Save
  // button on the User Group card (matches the per-section save UX). Group
  // is a broadcast-segmentation tag, not an engineer skill, so we keep it
  // out of the main Engineer Details patch.
  const changeGroup = useMutation({
    mutationFn: async () => {
      if (!form) return;
      await apiFetch(`/v1/admin/app-users/${id}/group`, {
        method: "POST",
        body: { group_name: form.group_name },
      });
    },
    onSuccess: () => {
      show("User group updated", "success");
      detail.refetch();
    },
    onError: (err) => show(friendlyError(err, "Group change failed"), "error"),
  });

  // Tax-info save — sends only the fields that changed (or that the admin
  // explicitly cleared). The API merges into the existing decrypted payload
  // before re-encrypting, so omitted fields keep their prior value.
  const saveTaxInfo = useMutation({
    mutationFn: async () => {
      if (!form || !original) return;
      const body: Record<string, unknown> = {};
      const trimOrNull = (v: string) => {
        const t = v.trim();
        return t.length === 0 ? null : t;
      };
      if (form.legal_name !== original.legal_name) {
        body.legal_name = trimOrNull(form.legal_name);
      }
      if (form.hkid !== original.hkid) {
        body.hkid = trimOrNull(form.hkid);
      }
      if (form.home_address !== original.home_address) {
        body.home_address = trimOrNull(form.home_address);
      }
      if (form.date_of_birth !== original.date_of_birth) {
        body.date_of_birth = trimOrNull(form.date_of_birth);
      }
      if (Object.keys(body).length === 0) {
        show("No tax info changes to save", "info");
        return;
      }
      await apiFetch(`/v1/admin/app-users/${id}/tax-info`, {
        method: "PATCH",
        body,
      });
    },
    onSuccess: () => {
      show("Payment & tax info updated", "success");
      detail.refetch();
    },
    onError: (err) =>
      show(friendlyError(err, "Failed to update tax info"), "error"),
  });

  if (detail.isLoading || !form) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Failed to load app user</Typography>
      </Box>
    );
  }

  const cardSx = {
    border: `1px solid ${tokens.colors.admin.border}`,
    borderRadius: 1,
    p: 3,
  };
  const sectionHeading = {
    fontSize: tokens.font.sizes.body,
    fontWeight: tokens.font.weights.semibold,
    color: tokens.colors.admin.textHeading,
    mb: 2,
  } as const;
  const baseBtn = {
    px: 4,
    textTransform: "none" as const,
    fontWeight: tokens.font.weights.medium,
    color: "#fff",
    boxShadow: "none",
  };
  const primaryBtn = {
    ...baseBtn,
    backgroundColor: "#007bff",
    "&:hover": { backgroundColor: "#0069d9", boxShadow: "none" },
    "&.Mui-disabled": {
      backgroundColor: "#7BB1F0",
      color: "rgba(255,255,255,0.85)",
    },
  };
  const greyBtn = {
    ...baseBtn,
    backgroundColor: "#D8D8D8",
    "&:hover": { backgroundColor: "#C8C8C8", boxShadow: "none" },
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        {/* Profile card */}
        <Paper elevation={0} sx={cardSx}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
              gap: 3,
              mb: 3,
            }}
          >
            <TextField
              variant="standard"
              fullWidth
              required
              label="User Name"
              helperText="Cannot be changed after creation"
              disabled
              value={form.username}
            />
            <TextField
              variant="standard"
              fullWidth
              required
              label="E-mail"
              type="email"
              inputProps={{ maxLength: 254 }}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <TextField
              variant="standard"
              fullWidth
              required
              select
              label="Status"
              value={form.is_active ? "active" : "inactive"}
              onChange={(e) =>
                setForm({
                  ...form,
                  is_active: e.target.value === "active",
                })
              }
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
            <TextField
              variant="standard"
              fullWidth
              required
              label="Role"
              disabled
              value={form.role === "customer" ? "Customer" : "Engineer"}
              helperText="App-user role can't be changed after creation"
            />
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
              gap: 3,
              mb: 3,
            }}
          >
            <TextField
              variant="standard"
              fullWidth
              required
              label="First Name"
              inputProps={{ maxLength: 80 }}
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
            />
            <TextField
              variant="standard"
              fullWidth
              required
              label="Last Name"
              inputProps={{ maxLength: 80 }}
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
            />
            <TextField
              variant="standard"
              fullWidth
              label="Phone"
              helperText="Phone is owned by the mobile sign-up flow"
              disabled
              value={form.phone}
            />
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              disabled={patch.isPending}
              sx={primaryBtn}
              onClick={() => patch.mutate()}
            >
              Save
            </Button>
            <Button
              variant="contained"
              sx={greyBtn}
              onClick={() => original && setForm(original)}
            >
              Reset
            </Button>
          </Stack>
        </Paper>

        {/* Engineer-only card */}
        {form.role === "engineer" && (
          <Paper elevation={0} sx={cardSx}>
            <Typography sx={sectionHeading}>Engineer Details</Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
                gap: 3,
                mb: 3,
              }}
            >
              <TextField
                variant="standard"
                fullWidth
                select
                label="Qualification"
                value={form.qualification_level}
                onChange={(e) =>
                  setForm({ ...form, qualification_level: e.target.value })
                }
              >
                <MenuItem value="primary">Primary</MenuItem>
                <MenuItem value="senior">Senior</MenuItem>
                <MenuItem value="expert">Expert</MenuItem>
              </TextField>
              <TextField
                variant="standard"
                fullWidth
                select
                label="Approval Status"
                value={form.approval_status}
                onChange={(e) =>
                  setForm({ ...form, approval_status: e.target.value })
                }
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </TextField>
              <TextField
                variant="standard"
                fullWidth
                label="Years of Experience"
                type="number"
                inputProps={{ min: 0, max: 70 }}
                value={form.work_experience_years}
                onChange={(e) =>
                  setForm({ ...form, work_experience_years: e.target.value })
                }
              />
            </Box>
            <Box sx={{ mb: 3 }}>
              <TextField
                variant="standard"
                fullWidth
                multiline
                minRows={2}
                label="Bio"
                helperText="Self-described summary; engineer-editable from the mobile app"
                inputProps={{ maxLength: 2000 }}
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
              />
            </Box>
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontSize: tokens.font.sizes.bodySm,
                  color: tokens.colors.admin.textMuted,
                  mb: 1,
                }}
              >
                Service Areas (HK 18 districts) — click to toggle
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {hkDistricts.map((d) => {
                  const selected = form.service_areas.includes(d);
                  return (
                    <Chip
                      key={d}
                      label={d}
                      clickable
                      color={selected ? "primary" : "default"}
                      variant={selected ? "filled" : "outlined"}
                      onClick={() =>
                        setForm({
                          ...form,
                          service_areas: selected
                            ? form.service_areas.filter((a) => a !== d)
                            : [...form.service_areas, d],
                        })
                      }
                    />
                  );
                })}
              </Stack>
            </Box>
            {form.approval_status === "rejected" &&
              original?.approval_status !== "rejected" && (
                <Box sx={{ mb: 3 }}>
                  <TextField
                    variant="standard"
                    fullWidth
                    required
                    label="Rejection Reason"
                    helperText="Required when moving to Rejected"
                    inputProps={{ maxLength: 1000 }}
                    value={form.reason}
                    onChange={(e) =>
                      setForm({ ...form, reason: e.target.value })
                    }
                  />
                </Box>
              )}
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                disabled={patch.isPending}
                sx={primaryBtn}
                onClick={() => patch.mutate()}
              >
                Save
              </Button>
              <Button
                variant="contained"
                sx={greyBtn}
                onClick={() => original && setForm(original)}
              >
                Reset
              </Button>
            </Stack>
          </Paper>
        )}

        {/* Payment & Tax Info — engineer-only, encrypted at rest
            (migration 00032). Engineers email/phone these in after
            registering; admin enters them here. HKID + Home Address +
            DOB are masked by default with eye-icon reveal; Legal Name
            is shown plain because admins need to read it for ID matching. */}
        {form.role === "engineer" && (
          <Paper elevation={0} sx={cardSx}>
            <Typography sx={sectionHeading}>Payment &amp; Tax Info</Typography>
            <Typography
              sx={{
                fontSize: tokens.font.sizes.bodySm,
                color: tokens.colors.admin.textMuted,
                mb: 2,
              }}
            >
              Used for commission payment and IR56-style tax filing. Stored
              encrypted; do not enter values you cannot verify.
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 3,
                mb: 3,
              }}
            >
              <TextField
                variant="standard"
                fullWidth
                label="Legal Full Name"
                helperText="As printed on the HKID; may differ from app display name"
                inputProps={{ maxLength: 120 }}
                value={form.legal_name}
                onChange={(e) =>
                  setForm({ ...form, legal_name: e.target.value })
                }
              />
              <TextField
                variant="standard"
                fullWidth
                label="Date of Birth"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.date_of_birth}
                onChange={(e) =>
                  setForm({ ...form, date_of_birth: e.target.value })
                }
              />
              <SensitiveTextField
                label="HKID"
                value={form.hkid}
                onChange={(v) => setForm({ ...form, hkid: v })}
                placeholder="A123456(7)"
                helperText="Format: 1–2 letters + 6 digits + check digit"
                maxLength={20}
              />
              <SensitiveTextField
                label="Home Address"
                value={form.home_address}
                onChange={(v) => setForm({ ...form, home_address: v })}
                helperText="Full residential address used for tax correspondence"
                maxLength={500}
              />
            </Box>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                disabled={saveTaxInfo.isPending}
                sx={primaryBtn}
                onClick={() => saveTaxInfo.mutate()}
              >
                Save
              </Button>
              <Button
                variant="contained"
                sx={greyBtn}
                onClick={() => original && setForm({ ...form,
                  legal_name: original.legal_name,
                  hkid: original.hkid,
                  home_address: original.home_address,
                  date_of_birth: original.date_of_birth,
                })}
              >
                Reset
              </Button>
            </Stack>
          </Paper>
        )}

        {/* Bank Info on File — engineer-only, read-only. Sourced from the
            most recent withdrawal_request (no separate engineer-level
            bank field exists). Account number is last-4-masked by default
            with eye-icon reveal. */}
        {form.role === "engineer" && (
          <Paper elevation={0} sx={cardSx}>
            <Typography sx={sectionHeading}>Bank Info on File</Typography>
            <Typography
              sx={{
                fontSize: tokens.font.sizes.bodySm,
                color: tokens.colors.admin.textMuted,
                mb: 2,
              }}
            >
              {detail.data?.latest_bank_info
                ? `Captured at most recent withdrawal request on ${
                    detail.data.latest_bank_info.captured_at?.slice(0, 10) ?? ""
                  }.`
                : "No bank info on file yet — captured at the engineer's first withdrawal."}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                gap: 3,
                mb: 1,
              }}
            >
              <SensitiveDisplay
                label="Bank Name"
                value={detail.data?.latest_bank_info?.bank_name ?? null}
                mask="full"
              />
              <SensitiveDisplay
                label="Account Holder"
                value={detail.data?.latest_bank_info?.account_holder ?? null}
                mask="full"
              />
              <SensitiveDisplay
                label="Account Number"
                value={detail.data?.latest_bank_info?.account_number ?? null}
                mask="last4"
              />
            </Box>
          </Paper>
        )}

        {/* User Group card — engineer + customer. Broadcast-segmentation
            tag (not skill level / tier), so it lives in its own section
            with its own Save button. Posts to /v1/admin/app-users/:id/group;
            server routes the UPDATE to engineer_profiles vs
            customer_profiles based on the target's role. */}
        {(form.role === "engineer" || form.role === "customer") && (
          <Paper elevation={0} sx={cardSx}>
            <Typography sx={sectionHeading}>User Group</Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
                gap: 3,
                mb: 3,
              }}
            >
              <TextField
                variant="standard"
                fullWidth
                label="Original User Group"
                disabled
                value={original?.group_name ?? ""}
              />
              <TextField
                variant="standard"
                fullWidth
                required
                select
                label="New User Group"
                value={form.group_name}
                onChange={(e) =>
                  setForm({ ...form, group_name: e.target.value })
                }
              >
                <MenuItem value="Primary">Primary</MenuItem>
                <MenuItem value="Secondary">Secondary</MenuItem>
              </TextField>
            </Box>
            <Button
              variant="contained"
              disabled={
                changeGroup.isPending ||
                !form.group_name ||
                form.group_name === original?.group_name
              }
              sx={primaryBtn}
              onClick={() => changeGroup.mutate()}
            >
              Save
            </Button>
          </Paper>
        )}
      </Stack>
      {ToastEl}
    </Box>
  );
}
